import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  ReactNode,
} from 'react';
import { updateUser } from '../api/user';
import { UserCacheData, UserInfo, OrgInfo, DepartmentUserInfo, UserCurrency } from '../types/auth';
import {
  clearUserCache as clearUserCacheFromStorage,
  getUserCacheData,
  setUserCacheData,
  USER_CACHE_KEYS,
} from '../utils/userUtils';
import { withoutUserLastUsedScheduleEntryByScheduleId } from '../utils/resolveUserLastUsed';
import type { UserAppConfig } from '../types/userAppConfig';
import {
  mergeUserAppConfig,
  resolveUserAppConfig,
  serializeUserAppConfig,
} from '../utils/userAppConfig';

/** 与服务器存储的用户行为记忆一致 */
export interface ServerSyncedBehaviorData {
  userLastUsedFilmID?: string | null;
  userLastUsedBudgetID?: string | null;
  userLastUsedBudgetItemID?: string[] | null;
  userLastUsedBreakdownID?: string | null;
  /** 每项 `breakdownID;sceneID`，按 breakdownID（首段）去重；与 setServerSyncedField 单条写入一致 */
  userLastUsedSceneID?: string[] | null;
  /** 每项 `breakdownID;elementID`，按 breakdownID（首段）去重 */
  userLastUsedElementID?: string[] | null;
  userLastUsedScheduleID?: string[] | null;
  /** 与 UserInfo / users 表字段同名：最后一席 slot 记忆（拍摄计划下的任务实体） */
  userLastUsedSlotID?: string[] | null;
  /** 与 UserInfo / users 表字段同名：最后一席 calDate 记忆（拍摄计划下的日历日实体） */
  userLastUsedCalDateID?: string[] | null;
  userLastUsedCalendarDate?: string[] | null;
  userLastUsedCurrency?: UserCurrency | null;
}

/** 仅本地缓存的行为数据 */
export interface LocalOnlyBehaviorData {
  userLastUsedBudgetItemField?: string[] | null;
  userLastUsedBreakdownField?: string[] | null;
  userLastUsedElementField?: string[] | null;
  userLastUsedBreakdownTab?: string[] | null;
  userLastUsedBudgetTab?: string[] | null;
  /** 拍摄计划页右侧 TabPages 选中项记忆（每项 `scheduleID;tabId`） */
  userLastUsedScheduleTab?: string[] | null;
  /** 拍摄计划页「场次列表」子 Tab + 排序记忆（每项 `scheduleID;tab,sortKey`） */
  userLastUsedScheduleScenesListTab?: string[] | null;
  /** 场次表排序记忆：每项 `breakdownID;排序字段;asc|desc`（与场号等列 key 一致），按 breakdownID 去重 */
  userLastUsedScenesTableSort?: string[] | null;
  userLastUsedBreakdownCurrentStateAndPosition?: import('../pages/Breakdown/shortcut/useUnifiedNavigationState').CurrentStateAndPosition | null;
}

export interface UserBehaviorData extends ServerSyncedBehaviorData, LocalOnlyBehaviorData {}

interface UserBehaviorRecord {
  serverSyncedData: ServerSyncedBehaviorData;
  localOnlyData: LocalOnlyBehaviorData;
}

type AllUsersBehaviorData = Record<string, UserBehaviorRecord>;

const BEHAVIOR_STORAGE_KEYS = {
  ALL_USERS_BEHAVIOR_DATA: 'userBehaviorAllUsersData',
  CURRENT_USER_ID: 'userBehaviorCurrentUserID',
} as const;

const loadAllUsersDataFromStorage = (): AllUsersBehaviorData => {
  try {
    const stored = localStorage.getItem(BEHAVIOR_STORAGE_KEYS.ALL_USERS_BEHAVIOR_DATA);
    if (stored) {
      return JSON.parse(stored) as AllUsersBehaviorData;
    }
  } catch (error) {
    console.error('加载所有用户行为缓存数据失败:', error);
  }
  return {};
};

const saveAllUsersDataToStorage = (data: AllUsersBehaviorData): void => {
  try {
    localStorage.setItem(BEHAVIOR_STORAGE_KEYS.ALL_USERS_BEHAVIOR_DATA, JSON.stringify(data));
  } catch (error) {
    console.error('保存所有用户行为缓存数据失败:', error);
  }
};

const saveCurrentUserIDToStorage = (userID: string | null): void => {
  try {
    if (userID) localStorage.setItem(BEHAVIOR_STORAGE_KEYS.CURRENT_USER_ID, userID);
    else localStorage.removeItem(BEHAVIOR_STORAGE_KEYS.CURRENT_USER_ID);
  } catch (error) {
    console.error('保存当前用户ID失败:', error);
  }
};

const SYNC_DEBOUNCE_DELAY = 1000;

const ARRAY_FIELDS: (keyof ServerSyncedBehaviorData)[] = [
  'userLastUsedBudgetItemID',
  'userLastUsedSceneID',
  'userLastUsedElementID',
  'userLastUsedScheduleID',
  'userLastUsedSlotID',
  'userLastUsedCalDateID',
  'userLastUsedCalendarDate',
];

const serializeArrayField = (value: string[] | null | undefined): string | null | undefined => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.length > 0 ? JSON.stringify(value) : null;
  return value as unknown as string;
};

const deserializeArrayField = (value: string | null | undefined): string[] | null | undefined => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      const items = trimmed.split('\n').filter((item) => item.trim() !== '');
      return items.length > 0 ? items : null;
    }
  }
  if (Array.isArray(value)) return value;
  return null;
};

const extractFirstID = (item: string): string | null => {
  if (!item || typeof item !== 'string') return null;
  const parts = item.split(';');
  return parts.length > 0 ? parts[0].trim() : null;
};

const updateArrayFieldWithDeduplication = (
  currentArray: string[] | null | undefined,
  newValue: string | null | undefined
): string[] | null | undefined => {
  if (!newValue || typeof newValue !== 'string' || newValue.trim() === '') {
    return currentArray || null;
  }
  const trimmedNewValue = newValue.trim();
  const newFirstID = extractFirstID(trimmedNewValue);
  if (!newFirstID) return currentArray || null;
  if (!currentArray || currentArray.length === 0) return [trimmedNewValue];
  const filteredArray = currentArray.filter((item) => extractFirstID(item) !== newFirstID);
  return [trimmedNewValue, ...filteredArray];
};

const toUserFieldValue = <K extends keyof ServerSyncedBehaviorData>(
  field: K,
  value: ServerSyncedBehaviorData[K]
): UserInfo[K] => {
  if (ARRAY_FIELDS.includes(field)) {
    return serializeArrayField(value as string[] | null | undefined) as UserInfo[K];
  }
  return value as UserInfo[K];
};

function buildServerSyncedPayloadFromUser(user: UserInfo): Record<string, unknown> {
  return {
    userLastUsedFilmID: user.userLastUsedFilmID || null,
    userLastUsedBudgetID: user.userLastUsedBudgetID || null,
    userLastUsedBudgetItemID: user.userLastUsedBudgetItemID,
    userLastUsedBreakdownID: user.userLastUsedBreakdownID || null,
    userLastUsedSceneID: user.userLastUsedSceneID,
    userLastUsedElementID: user.userLastUsedElementID,
    userLastUsedScheduleID: user.userLastUsedScheduleID,
    userLastUsedSlotID: user.userLastUsedSlotID,
    userLastUsedCalDateID: user.userLastUsedCalDateID,
    userLastUsedCalendarDate: user.userLastUsedCalendarDate,
    userLastUsedCurrency: user.userLastUsedCurrency || null,
  };
}

export interface CurrentUserContextType {
  userCache: UserCacheData | null;
  getUserCache: () => UserCacheData | null;
  setUserCache: (data: UserCacheData) => void;
  clearUserCache: () => void;
  getUserInfo: () => UserInfo;
  getAccessToken: () => string;
  getCurrentUserID: () => string;
  getCurrentUserOrgID: () => string | null;
  getCurrentUserOrganization: () => OrgInfo | null;
  getCurrentUserDepartments: () => DepartmentUserInfo[];
  isUserLoggedIn: () => boolean;
  currentUserID: string | null;
  serverSyncedData: ServerSyncedBehaviorData;
  localOnlyData: LocalOnlyBehaviorData;
  setCurrentUserID: (userID: string | null) => void;
  setServerSyncedField: <K extends keyof ServerSyncedBehaviorData>(
    field: K,
    value: string | ServerSyncedBehaviorData[K]
  ) => void;
  setServerSyncedData: (data: Partial<ServerSyncedBehaviorData>) => void;
  /** 从行为记忆中移除指向该拍摄计划 ID 的项（拍摄计划已删除或确认无效时） */
  stripServerSyncedUserLastUsedScheduleByScheduleId: (scheduleId: string) => void;
  setLocalOnlyField: <K extends keyof LocalOnlyBehaviorData>(
    field: K,
    value: string | LocalOnlyBehaviorData[K]
  ) => void;
  setLocalOnlyData: (data: Partial<LocalOnlyBehaviorData>) => void;
  initializeFromServer: (userID: string, serverData: Partial<ServerSyncedBehaviorData>) => void;
  getBehaviorData: () => UserBehaviorData;
  getUserBehaviorData: (userID: string) => UserBehaviorData | null;
  getAllCachedUserIDs: () => string[];
  getResolvedUserAppConfig: (fallbackLanguage: string) => UserAppConfig;
  updateUserAppConfig: (partial: Partial<UserAppConfig>) => void;
  /** 立即将待同步的 userAppConfig 写入服务器（关闭设置面板、离开页面前调用） */
  flushUserAppConfigSync: () => void;
}

const CurrentUserContext = createContext<CurrentUserContextType | undefined>(undefined);

export const CurrentUserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [userCache, setUserCacheState] = useState<UserCacheData | null>(() => {
    try {
      const cacheStr = localStorage.getItem(USER_CACHE_KEYS.USER_CACHE);
      if (!cacheStr) return null;
      return JSON.parse(cacheStr) as UserCacheData;
    } catch (error) {
      console.error('解析用户缓存数据失败:', error);
      return null;
    }
  });

  const [currentUserID, setCurrentUserIDState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(BEHAVIOR_STORAGE_KEYS.CURRENT_USER_ID);
    } catch {
      return null;
    }
  });

  const [allUsersData, setAllUsersDataState] = useState<AllUsersBehaviorData>(() =>
    loadAllUsersDataFromStorage()
  );

  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSyncDataRef = useRef<Partial<ServerSyncedBehaviorData>>({});
  const isSyncingRef = useRef(false);
  const lastHydratedUserIdRef = useRef<string | null>(null);
  const appConfigSyncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestAppConfigSyncRef = useRef<{ userID: string; userAppConfig: string } | null>(null);
  const isAppConfigSyncingRef = useRef(false);

  useEffect(() => {
    if (userCache) {
      try {
        localStorage.setItem(USER_CACHE_KEYS.USER_CACHE, JSON.stringify(userCache));
        localStorage.setItem('access_token', userCache.accessToken);
      } catch (error) {
        console.error('存储用户缓存数据失败:', error);
      }
    } else {
      localStorage.removeItem(USER_CACHE_KEYS.USER_CACHE);
      localStorage.removeItem('user');
      localStorage.removeItem('access_token');
    }
  }, [userCache]);

  const updateUserData = useCallback((userID: string, updater: (prev: UserBehaviorRecord) => UserBehaviorRecord) => {
    setAllUsersDataState((prev) => {
      const currentRecord = prev[userID] || { serverSyncedData: {}, localOnlyData: {} };
      const newRecord = updater(currentRecord);
      const newData = { ...prev, [userID]: newRecord };
      saveAllUsersDataToStorage(newData);
      return newData;
    });
  }, []);

  const syncToServer = useCallback(async (userID: string, data: Partial<ServerSyncedBehaviorData>) => {
    if (isSyncingRef.current) {
      pendingSyncDataRef.current = { ...pendingSyncDataRef.current, ...data };
      return;
    }
    try {
      isSyncingRef.current = true;
      const mergedData = { ...pendingSyncDataRef.current, ...data };
      pendingSyncDataRef.current = {};
      const dataToSync: Record<string, unknown> = {};
      Object.keys(mergedData).forEach((key) => {
        const fieldKey = key as keyof ServerSyncedBehaviorData;
        const value = mergedData[fieldKey];
        if (ARRAY_FIELDS.includes(fieldKey)) {
          if (value === null) {
            dataToSync[fieldKey] = null;
          } else if (typeof value === 'string') {
            dataToSync[fieldKey] = value;
          } else if (value !== undefined) {
            dataToSync[fieldKey] = serializeArrayField(value as string[] | null | undefined);
          }
          return;
        }
        if (value !== null && value !== undefined) {
          dataToSync[fieldKey] = value;
        }
      });
      if (Object.keys(dataToSync).length === 0) return;
      await updateUser(userID, dataToSync);
    } catch (error) {
      console.error('同步用户行为记忆到服务器失败:', error);
      pendingSyncDataRef.current = { ...pendingSyncDataRef.current, ...data };
    } finally {
      isSyncingRef.current = false;
    }
  }, []);

  const debouncedSyncToServer = useCallback(
    (userID: string, data: Partial<ServerSyncedBehaviorData>) => {
      pendingSyncDataRef.current = { ...pendingSyncDataRef.current, ...data };
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
      syncTimerRef.current = setTimeout(() => {
        syncToServer(userID, pendingSyncDataRef.current);
        pendingSyncDataRef.current = {};
      }, SYNC_DEBOUNCE_DELAY);
    },
    [syncToServer]
  );

  const initializeFromServer = useCallback(
    (userID: string, serverData: Record<string, unknown>) => {
      const deserializedData: Record<string, unknown> = {};
      Object.keys(serverData).forEach((key) => {
        const fieldKey = key as keyof ServerSyncedBehaviorData;
        const value = serverData[fieldKey];
        if (ARRAY_FIELDS.includes(fieldKey)) {
          deserializedData[fieldKey] = deserializeArrayField(value as string | null | undefined);
        } else {
          deserializedData[fieldKey] = value;
        }
      });
      updateUserData(userID, (prev) => ({
        ...prev,
        serverSyncedData: {
          ...prev.serverSyncedData,
          ...deserializedData,
        } as ServerSyncedBehaviorData,
      }));
      pendingSyncDataRef.current = {};
    },
    [updateUserData]
  );

  /** 登录或恢复会话：用 user 对象初始化行为缓存（不触发同步） */
  useEffect(() => {
    const uid = userCache?.user?.userID;
    if (!uid) {
      lastHydratedUserIdRef.current = null;
      return;
    }
    if (lastHydratedUserIdRef.current === uid) return;
    lastHydratedUserIdRef.current = uid;
    initializeFromServer(uid, buildServerSyncedPayloadFromUser(userCache.user));
  }, [userCache, initializeFromServer]);

  const serverSyncedData: ServerSyncedBehaviorData = useMemo(() => {
    if (currentUserID && allUsersData[currentUserID]?.serverSyncedData) {
      return allUsersData[currentUserID].serverSyncedData;
    }
    if (userCache?.user) {
      const userInfo = userCache.user;
      return {
        userLastUsedFilmID: userInfo.userLastUsedFilmID || null,
        userLastUsedBudgetID: userInfo.userLastUsedBudgetID || null,
        userLastUsedBudgetItemID: deserializeArrayField(userInfo.userLastUsedBudgetItemID),
        userLastUsedBreakdownID: userInfo.userLastUsedBreakdownID || null,
        userLastUsedSceneID: deserializeArrayField(userInfo.userLastUsedSceneID),
        userLastUsedElementID: deserializeArrayField(userInfo.userLastUsedElementID),
        userLastUsedScheduleID: deserializeArrayField(userInfo.userLastUsedScheduleID),
        userLastUsedSlotID: deserializeArrayField(userInfo.userLastUsedSlotID),
        userLastUsedCalDateID: deserializeArrayField(userInfo.userLastUsedCalDateID),
        userLastUsedCalendarDate: deserializeArrayField(userInfo.userLastUsedCalendarDate),
        userLastUsedCurrency: userInfo.userLastUsedCurrency || null,
      };
    }
    return {};
  }, [currentUserID, allUsersData, userCache]);

  const localOnlyData: LocalOnlyBehaviorData = useMemo(() => {
    if (!currentUserID) return {};
    return allUsersData[currentUserID]?.localOnlyData || {};
  }, [currentUserID, allUsersData]);

  const getUserCache = useCallback((): UserCacheData | null => userCache, [userCache]);

  const setUserCache = useCallback((data: UserCacheData): void => {
    setUserCacheState(data);
    try {
      setUserCacheData(data);
    } catch (error) {
      console.error('同步用户缓存到 localStorage 失败:', error);
    }
  }, []);

  const clearUserCache = useCallback((): void => {
    lastHydratedUserIdRef.current = null;
    pendingSyncDataRef.current = {};
    if (syncTimerRef.current) {
      clearTimeout(syncTimerRef.current);
      syncTimerRef.current = null;
    }
    if (appConfigSyncTimerRef.current) {
      clearTimeout(appConfigSyncTimerRef.current);
      appConfigSyncTimerRef.current = null;
    }
    latestAppConfigSyncRef.current = null;
    setAllUsersDataState({});
    saveAllUsersDataToStorage({});
    clearUserCacheFromStorage();
    setUserCacheState(null);
  }, []);

  const getUserInfo = useCallback((): UserInfo => {
    if (!userCache?.user) throw new Error('未找到用户信息，请重新登录');
    return userCache.user;
  }, [userCache]);

  const getAccessToken = useCallback((): string => {
    if (!userCache?.accessToken) throw new Error('未找到访问令牌，请重新登录');
    return userCache.accessToken;
  }, [userCache]);

  const getCurrentUserID = useCallback((): string => getUserInfo().userID, [getUserInfo]);

  const getCurrentUserOrgID = useCallback((): string | null => getUserInfo().userOrgID || null, [getUserInfo]);

  const getCurrentUserOrganization = useCallback((): OrgInfo | null => {
    if (!userCache) throw new Error('未找到用户信息，请重新登录');
    return userCache.organization || null;
  }, [userCache]);

  const getCurrentUserDepartments = useCallback((): DepartmentUserInfo[] => {
    if (!userCache) throw new Error('未找到用户信息，请重新登录');
    return userCache.departments || [];
  }, [userCache]);

  const isUserLoggedIn = useCallback(
    (): boolean => !!(userCache?.user && userCache?.accessToken),
    [userCache]
  );

  const setCurrentUserID = useCallback((userID: string | null) => {
    setCurrentUserIDState(userID);
    saveCurrentUserIDToStorage(userID);
  }, []);

  const syncBehaviorDataToUserCache = useCallback(
    (userID: string, data: Partial<ServerSyncedBehaviorData>) => {
      if (!userID || Object.keys(data).length === 0) return;
      setUserCacheState((prev) => {
        if (!prev?.user || prev.user.userID !== userID) return prev;
        const nextUser = { ...prev.user } as UserInfo;
        (Object.keys(data) as (keyof ServerSyncedBehaviorData)[]).forEach((field) => {
          const value = data[field];
          if (value === undefined) return;
          (nextUser as unknown as Record<string, unknown>)[field] = toUserFieldValue(
            field,
            value as ServerSyncedBehaviorData[keyof ServerSyncedBehaviorData]
          ) as unknown;
        });
        return { ...prev, user: nextUser };
      });
    },
    []
  );

  const setServerSyncedField = useCallback(
    <K extends keyof ServerSyncedBehaviorData>(field: K, value: string | ServerSyncedBehaviorData[K]) => {
      let effectiveUserID = currentUserID;
      if (!effectiveUserID && userCache?.user?.userID) {
        effectiveUserID = userCache.user.userID;
        setCurrentUserIDState(effectiveUserID);
        saveCurrentUserIDToStorage(effectiveUserID);
      }
      if (!effectiveUserID) {
        console.warn('无法设置行为数据：当前用户ID为空');
        return;
      }
      const userID = effectiveUserID;
      let resolvedValue: ServerSyncedBehaviorData[K] | undefined;
      updateUserData(userID, (prev) => {
        let newValue: ServerSyncedBehaviorData[K];
        if (ARRAY_FIELDS.includes(field)) {
          const currentArray = prev.serverSyncedData[field] as string[] | null | undefined;
          if (typeof value === 'string') {
            newValue = updateArrayFieldWithDeduplication(currentArray, value) as ServerSyncedBehaviorData[K];
          } else if (Array.isArray(value) && value.length > 0) {
            newValue = updateArrayFieldWithDeduplication(currentArray, value[0]) as ServerSyncedBehaviorData[K];
          } else if (value === null || value === undefined) {
            newValue = null as ServerSyncedBehaviorData[K];
          } else {
            newValue = value as ServerSyncedBehaviorData[K];
          }
        } else {
          newValue = value as ServerSyncedBehaviorData[K];
        }
        const syncValue = ARRAY_FIELDS.includes(field)
          ? serializeArrayField(newValue as string[] | null | undefined)
          : newValue;
        debouncedSyncToServer(userID, { [field]: syncValue } as Partial<ServerSyncedBehaviorData>);
        resolvedValue = newValue;
        return {
          ...prev,
          serverSyncedData: { ...prev.serverSyncedData, [field]: newValue },
        };
      });
      if (resolvedValue !== undefined) {
        syncBehaviorDataToUserCache(userID, { [field]: resolvedValue } as Partial<ServerSyncedBehaviorData>);
      }
    },
    [currentUserID, userCache, updateUserData, debouncedSyncToServer, syncBehaviorDataToUserCache]
  );

  const setServerSyncedData = useCallback(
    (data: Partial<ServerSyncedBehaviorData>) => {
      let effectiveUserID = currentUserID;
      if (!effectiveUserID && userCache?.user?.userID) {
        effectiveUserID = userCache.user.userID;
        setCurrentUserIDState(effectiveUserID);
        saveCurrentUserIDToStorage(effectiveUserID);
      }
      if (!effectiveUserID) {
        console.warn('无法设置行为数据：当前用户ID为空');
        return;
      }
      const userID = effectiveUserID;
      const resolvedData: Record<string, unknown> = {};
      updateUserData(userID, (prev) => {
        const updatedServerSyncedData: Record<string, unknown> = {};
        const syncData: Record<string, unknown> = {};
        Object.keys(data).forEach((key) => {
          const field = key as keyof ServerSyncedBehaviorData;
          const value = data[field];
          if (ARRAY_FIELDS.includes(field)) {
            const currentArray = prev.serverSyncedData[field] as string[] | null | undefined;
            let newArrayValue: string[] | null | undefined;
            if (typeof value === 'string') {
              newArrayValue = updateArrayFieldWithDeduplication(currentArray, value);
            } else if (Array.isArray(value) && value.length > 0) {
              newArrayValue = updateArrayFieldWithDeduplication(currentArray, value[0]);
            } else if (value === null || value === undefined) {
              newArrayValue = null;
            } else {
              newArrayValue = value as string[] | null | undefined;
            }
            updatedServerSyncedData[field] = newArrayValue;
            syncData[field] = serializeArrayField(newArrayValue);
          } else {
            updatedServerSyncedData[field] = value;
            syncData[field] = value;
          }
          resolvedData[field] = updatedServerSyncedData[field];
        });
        debouncedSyncToServer(userID, syncData as Partial<ServerSyncedBehaviorData>);
        return {
          ...prev,
          serverSyncedData: { ...prev.serverSyncedData, ...updatedServerSyncedData } as ServerSyncedBehaviorData,
        };
      });
      syncBehaviorDataToUserCache(userID, resolvedData as Partial<ServerSyncedBehaviorData>);
    },
    [currentUserID, userCache, updateUserData, debouncedSyncToServer, syncBehaviorDataToUserCache]
  );

  const stripServerSyncedUserLastUsedScheduleByScheduleId = useCallback(
    (scheduleId: string) => {
      const sid = scheduleId?.trim();
      if (!sid) return;
      let effectiveUserID = currentUserID;
      if (!effectiveUserID && userCache?.user?.userID) {
        effectiveUserID = userCache.user.userID;
        setCurrentUserIDState(effectiveUserID);
        saveCurrentUserIDToStorage(effectiveUserID);
      }
      if (!effectiveUserID) {
        console.warn('无法更新行为数据：当前用户ID为空');
        return;
      }
      const userID = effectiveUserID;
      let resolvedValue: string[] | null | undefined;
      updateUserData(userID, (prev) => {
        const currentArray = prev.serverSyncedData.userLastUsedScheduleID;
        const newValue = withoutUserLastUsedScheduleEntryByScheduleId(currentArray, sid);
        resolvedValue = newValue ?? null;
        debouncedSyncToServer(userID, {
          userLastUsedScheduleID: serializeArrayField(newValue),
        } as Partial<ServerSyncedBehaviorData>);
        return {
          ...prev,
          serverSyncedData: {
            ...prev.serverSyncedData,
            userLastUsedScheduleID: newValue,
          },
        };
      });
      if (resolvedValue !== undefined) {
        syncBehaviorDataToUserCache(userID, {
          userLastUsedScheduleID: resolvedValue,
        } as Partial<ServerSyncedBehaviorData>);
      }
    },
    [currentUserID, userCache, updateUserData, debouncedSyncToServer, syncBehaviorDataToUserCache]
  );

  const setLocalOnlyField = useCallback(
    <K extends keyof LocalOnlyBehaviorData>(field: K, value: string | LocalOnlyBehaviorData[K]) => {
      if (!currentUserID) {
        console.warn('无法设置行为数据：当前用户ID为空');
        return;
      }
      updateUserData(currentUserID, (prev) => {
        if (field === 'userLastUsedBreakdownCurrentStateAndPosition') {
          return {
            ...prev,
            localOnlyData: { ...prev.localOnlyData, [field]: value as LocalOnlyBehaviorData[K] },
          };
        }
        const currentArray = prev.localOnlyData[field] as string[] | null | undefined;
        let newValue: string[] | null | undefined;
        if (typeof value === 'string') {
          newValue = updateArrayFieldWithDeduplication(currentArray, value);
        } else if (Array.isArray(value) && value.length > 0) {
          newValue = updateArrayFieldWithDeduplication(currentArray, value[0]);
        } else if (value === null || value === undefined) {
          newValue = null;
        } else {
          newValue = value as string[] | null | undefined;
        }
        return {
          ...prev,
          localOnlyData: { ...prev.localOnlyData, [field]: newValue as LocalOnlyBehaviorData[K] },
        };
      });
    },
    [currentUserID, updateUserData]
  );

  const setLocalOnlyData = useCallback(
    (data: Partial<LocalOnlyBehaviorData>) => {
      if (!currentUserID) {
        console.warn('无法设置行为数据：当前用户ID为空');
        return;
      }
      updateUserData(currentUserID, (prev) => {
        const updatedLocalOnlyData: Record<string, unknown> = {};
        Object.keys(data).forEach((key) => {
          const field = key as keyof LocalOnlyBehaviorData;
          const value = data[field];
          const currentArray = prev.localOnlyData[field] as string[] | null | undefined;
          if (typeof value === 'string') {
            updatedLocalOnlyData[field] = updateArrayFieldWithDeduplication(currentArray, value);
          } else if (Array.isArray(value) && value.length > 0) {
            updatedLocalOnlyData[field] = updateArrayFieldWithDeduplication(currentArray, value[0]);
          } else if (value === null || value === undefined) {
            updatedLocalOnlyData[field] = null;
          } else {
            updatedLocalOnlyData[field] = value;
          }
        });
        return {
          ...prev,
          localOnlyData: { ...prev.localOnlyData, ...updatedLocalOnlyData } as LocalOnlyBehaviorData,
        };
      });
    },
    [currentUserID, updateUserData]
  );

  const getBehaviorData = useCallback((): UserBehaviorData => {
    return { ...serverSyncedData, ...localOnlyData };
  }, [serverSyncedData, localOnlyData]);

  const getUserBehaviorData = useCallback(
    (userID: string): UserBehaviorData | null => {
      const userData = allUsersData[userID];
      if (!userData) return null;
      return { ...userData.serverSyncedData, ...userData.localOnlyData };
    },
    [allUsersData]
  );

  const getAllCachedUserIDs = useCallback((): string[] => Object.keys(allUsersData), [allUsersData]);

  const getResolvedUserAppConfig = useCallback(
    (fallbackLanguage: string): UserAppConfig => {
      const raw =
        userCache?.user?.userAppConfig ?? getUserCacheData()?.user?.userAppConfig;
      return resolveUserAppConfig(raw, fallbackLanguage);
    },
    [userCache],
  );

  const runAppConfigSync = useCallback(async () => {
    if (isAppConfigSyncingRef.current) return;
    const pending = latestAppConfigSyncRef.current;
    if (!pending) return;

    isAppConfigSyncingRef.current = true;
    const { userID, userAppConfig } = pending;
    try {
      await updateUser(userID, { userAppConfig });
      if (latestAppConfigSyncRef.current?.userAppConfig === userAppConfig) {
        latestAppConfigSyncRef.current = null;
      }
    } catch (error) {
      console.error('同步 userAppConfig 到服务器失败:', error);
    } finally {
      isAppConfigSyncingRef.current = false;
      if (latestAppConfigSyncRef.current) {
        void runAppConfigSync();
      }
    }
  }, []);

  const queueAppConfigSync = useCallback(
    (userID: string, userAppConfig: string) => {
      latestAppConfigSyncRef.current = { userID, userAppConfig };
      if (appConfigSyncTimerRef.current) clearTimeout(appConfigSyncTimerRef.current);
      appConfigSyncTimerRef.current = setTimeout(() => {
        appConfigSyncTimerRef.current = null;
        void runAppConfigSync();
      }, SYNC_DEBOUNCE_DELAY);
    },
    [runAppConfigSync],
  );

  const flushUserAppConfigSync = useCallback(() => {
    if (appConfigSyncTimerRef.current) {
      clearTimeout(appConfigSyncTimerRef.current);
      appConfigSyncTimerRef.current = null;
    }
    if (latestAppConfigSyncRef.current) {
      void runAppConfigSync();
    }
  }, [runAppConfigSync]);

  const updateUserAppConfig = useCallback(
    (partial: Partial<UserAppConfig>) => {
      const cache = userCache ?? getUserCacheData();
      const user = cache?.user;
      if (!user?.userID) {
        console.warn('无法更新 userAppConfig：当前用户未登录');
        return;
      }
      const current = resolveUserAppConfig(user.userAppConfig, 'zh-CN');
      const merged = mergeUserAppConfig(current, partial);
      const serialized = serializeUserAppConfig(merged);
      setUserCacheState((prev) => {
        const base = prev ?? cache;
        if (!base) return null;
        return {
          ...base,
          user: { ...base.user, userAppConfig: serialized },
        };
      });
      queueAppConfigSync(user.userID, serialized);
    },
    [userCache, queueAppConfigSync],
  );

  useEffect(() => {
    if (userCache?.user) {
      const userID = userCache.user.userID;
      if (userID !== currentUserID) {
        setCurrentUserIDState(userID);
        saveCurrentUserIDToStorage(userID);
      }
    } else if (userCache === null && currentUserID !== null) {
      setCurrentUserIDState(null);
      saveCurrentUserIDToStorage(null);
    }
  }, [userCache, currentUserID]);

  useEffect(() => {
    const onBeforeUnload = () => {
      if (appConfigSyncTimerRef.current) {
        clearTimeout(appConfigSyncTimerRef.current);
        appConfigSyncTimerRef.current = null;
      }
      void runAppConfigSync();
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (syncTimerRef.current) {
        clearTimeout(syncTimerRef.current);
        syncTimerRef.current = null;
      }
      if (appConfigSyncTimerRef.current) {
        clearTimeout(appConfigSyncTimerRef.current);
        appConfigSyncTimerRef.current = null;
      }
      void runAppConfigSync();
    };
  }, [runAppConfigSync]);

  const value: CurrentUserContextType = {
    userCache,
    getUserCache,
    setUserCache,
    clearUserCache,
    getUserInfo,
    getAccessToken,
    getCurrentUserID,
    getCurrentUserOrgID,
    getCurrentUserOrganization,
    getCurrentUserDepartments,
    isUserLoggedIn,
    currentUserID,
    serverSyncedData,
    localOnlyData,
    setCurrentUserID,
    setServerSyncedField: setServerSyncedField as CurrentUserContextType['setServerSyncedField'],
    setServerSyncedData,
    stripServerSyncedUserLastUsedScheduleByScheduleId,
    setLocalOnlyField: setLocalOnlyField as CurrentUserContextType['setLocalOnlyField'],
    setLocalOnlyData,
    initializeFromServer: initializeFromServer as CurrentUserContextType['initializeFromServer'],
    getBehaviorData,
    getUserBehaviorData,
    getAllCachedUserIDs,
    getResolvedUserAppConfig,
    updateUserAppConfig,
    flushUserAppConfigSync,
  };

  return <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>;
};

export const useCurrentUser = (): CurrentUserContextType => {
  const context = useContext(CurrentUserContext);
  if (context === undefined) {
    throw new Error('useCurrentUser must be used within a CurrentUserProvider');
  }
  return context;
};
